import { isEmpty } from 'lodash-es';
import {
  ISubmissionSettingsResponse,
  ISubmissionSettingsObject,
  IMappingFormSettingsResponse,
  IMappingFormSetting,
} from 'wisestep-api';
import { ISubmissionSettingContext } from 'wisestep-base';
import * as Yup from 'yup';
import { array, object, string, number, date, mixed } from 'yup';

const singleWsButtonGroup = object().shape({
  index: number(),
  key: string(),
});

const multipleWsButtonGroup = object().shape({
  index: array().of(number()),
  key: array().of(string()),
});

const locationSchema = array().of(
  object().shape({
    area: string().nullable(true),
    city: string().nullable(true),
    continent: string().nullable(true),
    continentCode: string().nullable(true),
    country: string().nullable(true),
    countryCode: string().nullable(true),
    lat: mixed()
      .nullable(true)
      .transform((value) => (!isEmpty(value) ? value : null)),
    lon: mixed()
      .nullable(true)
      .transform((value) => (!isEmpty(value) ? value : null)),
    placeId: string().nullable(true),
    point: string().nullable(true),
    state: string().nullable(true),
    stateCode: string().nullable(true),
    zipCode: string().nullable(true),
  })
);

const messengerSchema = array().of(
  object().shape({
    type: string().nullable(true),
    messengerId: string().nullable(true),
  })
);

const skillMatrixSchema = array().of(
  object().shape({
    skill: string().nullable(true),
    rating: number().nullable(true),
  })
);

const singleWsButtonGroupRequiredOrNot = (required: boolean, errMsg: string) =>
  required ? singleWsButtonGroup.required(errMsg) : singleWsButtonGroup.nullable(true);

const checkStringRequiredOrNot = (required: boolean, errMsg: string) =>
  required ? string().nullable(false).required(errMsg) : string().nullable(true);
const checkArrayRequiredOrNot = (required: boolean, errMsg: string, field: string) =>
  required
    ? array()
        .of(string().trim().required(`${field} cannot be empty`))
        .required(errMsg)
        .min(1, 'At least one value is required')
    : array().of(string()).nullable(true);
const checkEmailRequiredOrNot = (required: boolean, errMsg: string, profileUrl: string) => {
  if (!isEmpty(profileUrl)) {
    return string().nullable(true);
  } else {
    if (required) {
      return string().nullable(false).required(errMsg);
    } else {
      return string().nullable(true);
    }
  }
};

const isSubmissionSettingsObject = (setting: any): setting is ISubmissionSettingsObject => {
  return (
    setting &&
    typeof setting?.required === 'boolean' &&
    typeof setting?.disabled === 'boolean' &&
    typeof setting?.hidden === 'boolean'
  );
};
const isMappingSettingsObject = (setting: any): setting is IMappingFormSetting => {
  return (
    setting &&
    typeof setting?.fieldName === 'string' &&
    typeof setting?.show === 'boolean' &&
    typeof setting?.mandatory === 'boolean'
  );
};
const isRequired = (setting: ISubmissionSettingsObject | IMappingFormSetting | undefined) => {
  if (isSubmissionSettingsObject(setting))
    return setting != undefined && setting?.required && !setting?.hidden;
  if (isMappingSettingsObject(setting))
    return setting != undefined && setting?.show && setting?.mandatory;
  else return false;
};

const getCompensation = (required: boolean, reqMsg: string) => {
  if (required) {
    return object()
      .shape({
        currency: string(),
        value: number(),
        timeSpan: string(),
      })
      .required(reqMsg);
  }
  return object()
    .shape({
      currency: string().nullable(true),
      value: number().nullable(true),
      timeSpan: string().nullable(true),
    })
    .nullable(true);
};
export const mappingFormSchema = (
  setting: IMappingFormSettingsResponse | undefined,
  profileUrl: string
) => {
  return object().shape({
    name: checkStringRequiredOrNot(isRequired(setting?.name), 'Please enter name'),
    email: checkEmailRequiredOrNot(isRequired(setting?.email), 'Please enter email', profileUrl),
    phone: isRequired(setting?.phoneNumber)
      ? string()
          .matches(
            /^$|^[+(]?\(?\(?\d{1,4}?[-.()\s]?\(?\d{1,3}?\)?[-.()\s]?\d{1,4}[-.()\s]?\d{1,4}[-.()\s]?\d{1,9}[)\d]$/,
            'Please enter valid phone number'
          )
          .required('Please enter phone number')
      : string()
          .matches(
            /^$|^[+(]?\(?\(?\d{1,4}?[-.()\s]?\(?\d{1,3}?\)?[-.()\s]?\d{1,4}[-.()\s]?\d{1,4}[-.()\s]?\d{1,9}[)\d]$/,
            'Please enter valid phone number'
          )
          .nullable(true),
    alternateEmails: isRequired(setting?.alternateEmails)
      ? array().of(string()).required('Please enter alternate emails')
      : array().of(string()).nullable(true),
    alternatePhoneNumbers: isRequired(setting?.alternatePhoneNumber)
      ? array()
          .of(
            string().matches(
              /^$|^[+(]?\(?\(?\d{1,4}?[-.()\s]?\(?\d{1,3}?\)?[-.()\s]?\d{1,4}[-.()\s]?\d{1,4}[-.()\s]?\d{1,9}[)\d]$/,
              'Please enter valid phone number. The format of the preselected or entered phone number should be correct'
            )
          )
          .required('Please enter alternate phone number')
      : array()
          .of(
            string().matches(
              /^$|^[+(]?\(?\(?\d{1,4}?[-.()\s]?\(?\d{1,3}?\)?[-.()\s]?\d{1,4}[-.()\s]?\d{1,4}[-.()\s]?\d{1,9}[)\d]$/,
              'Please enter valid phone number. The format of the preselected or entered phone number should be correct'
            )
          )
          .nullable(true),
    socialLinks: isRequired(setting?.socialLinks)
      ? array()
          .of(string())
          .required('Please enter social links')
          .min(1, 'At least one social link is required')
      : array().of(string()).nullable(true),
    messengerLinks: isRequired(setting?.messengerLinks)
      ? messengerSchema.required('Please enter messenger id')
      : messengerSchema.nullable(true),
    currentJobType: singleWsButtonGroupRequiredOrNot(
      isRequired(setting?.currentJobType),
      'Please select job type'
    ),
    skills: isRequired(setting?.skills)
      ? array().of(string()).required('Please enter candidate skills')
      : array().of(string()).nullable(true),
    preferredLocation: locationSchema.nullable(true),
    currentLocation: isRequired(setting?.locations)
      ? locationSchema.required('Please enter candidate location')
      : locationSchema.nullable(true),
    currentJobIncludesTravel: singleWsButtonGroupRequiredOrNot(
      isRequired(setting?.jobIncludesTravel),
      'Do current job includes travel ?'
    ),
    currentSalary: getCompensation(
      isRequired(setting?.currentSalary),
      'Current salary of candidate is required'
    ),
    expectedSalary: getCompensation(
      isRequired(setting?.expectedSalary),
      'Expected salary of candidate is required'
    ),
    otherAllowance: checkStringRequiredOrNot(
      isRequired(setting?.otherAllowance),
      'Other allowances is required'
    ),
    expectedAllowance: checkStringRequiredOrNot(
      isRequired(setting?.expectedAllowance),
      'Expected allowances is required'
    ),
    willingToTravel: singleWsButtonGroupRequiredOrNot(
      isRequired(setting?.willingToTravel),
      'Is candidate willing to travel ?'
    ),
    availableFrom: isRequired(setting?.availableFrom) ? date().required() : date().nullable(true),
    noticePeriodInDays: singleWsButtonGroupRequiredOrNot(
      isRequired(setting?.noticePeriodInDays),
      'Please select notice period'
    ),
    interviewingWithOthers: singleWsButtonGroupRequiredOrNot(
      isRequired(setting?.interviewingWithOthers),
      'Is candidate interviewing else ?'
    ),
    authorizationCategory: singleWsButtonGroupRequiredOrNot(
      isRequired(setting?.workAuthorizationCategory),
      'Please select work authorization category'
    ),
    visaStatus: checkStringRequiredOrNot(isRequired(setting?.visaStatus), 'Enter visa status'),
    visaValidity: checkStringRequiredOrNot(
      isRequired(setting?.visaValidity),
      'Enter visa validity'
    ),
    nationality: string().nullable(true),
    tags: array().of(string()).nullable(true),
    source: checkStringRequiredOrNot(isRequired(setting?.source), 'Please enter source'),
    candidatePersonalId: checkStringRequiredOrNot(
      isRequired(setting?.candidatePersonalId),
      'Please enter candidate id'
    ),
    communicationPreference: isRequired(setting?.communicationPreference)
      ? multipleWsButtonGroup.required('Please select communication preference')
      : multipleWsButtonGroup.nullable(true),
    jobPreference: isRequired(setting?.jobPreference)
      ? multipleWsButtonGroup.required('Please select job preference')
      : multipleWsButtonGroup.nullable(true),
    expInYears: isRequired(setting?.totalExperience)
      ? number().required('Please enter total experience')
      : number().transform((value) =>
          isNaN(value) || value === null || value === undefined ? 0 : value
        ),
    expInMonth: number().transform((value) =>
      isNaN(value) || value === null || value === undefined ? 0 : value
    ),
    relExpInYears: isRequired(setting?.relevantExperience)
      ? number().required('Please enter relevant experience')
      : number().transform((value) =>
          isNaN(value) || value === null || value === undefined ? 0 : value
        ),
    relExpInMonth: number().transform((value) =>
      isNaN(value) || value === null || value === undefined ? 0 : value
    ),
    jobTitle: checkStringRequiredOrNot(
      isRequired(setting?.jobTitle),
      'Please enter candidate job title'
    ),
    currentCompany: checkStringRequiredOrNot(
      isRequired(setting?.currentCompany),
      'Please enter candidate current employer'
    ),
    jobChangeReason: checkStringRequiredOrNot(
      isRequired(setting?.jobChangeReason),
      'Please enter reason for change'
    ),
    recruiterAssessment: checkStringRequiredOrNot(
      isRequired(setting?.recruiterAssessment),
      'Please enter your assessment'
    ),
    relocationPreferences: checkStringRequiredOrNot(
      isRequired(setting?.relocationPreference),
      'Please enter relocation preference'
    ),
    willingToTravelType: checkStringRequiredOrNot(false, 'Is candidate willing to travel'),
    familyWillingToRelocate: singleWsButtonGroupRequiredOrNot(
      isRequired(setting?.familyWillingToRelocate),
      'Is family willing to relocate'
    ),
    prevJobTitles: isRequired(setting?.previousJobTitles)
      ? array().of(string()).required()
      : array().of(string()).nullable(true),
    prevEmployers: isRequired(setting?.prevEmployers)
      ? array().of(string()).required()
      : array().of(string()).nullable(true),
    highestEducation: isRequired(setting?.highestEducation)
      ? array().of(string()).required()
      : array().of(string()).nullable(true),
    institutesAttended: isRequired(setting?.institutesAttended)
      ? array().of(string()).required()
      : array().of(string()).nullable(true),
    certifications: isRequired(setting?.certifications)
      ? array().of(string()).required()
      : array().of(string()).nullable(true),
    passportNumber: checkStringRequiredOrNot(
      isRequired(setting?.passportNumber),
      'Enter Passport Number'
    ),
    passportType: checkStringRequiredOrNot(
      isRequired(setting?.passportType),
      'Enter Passport Type'
    ),
    ssnNumber: checkStringRequiredOrNot(isRequired(setting?.ssnNumber), 'Enter SSN Number'),
    panCard: checkStringRequiredOrNot(isRequired(setting?.panCard), 'Enter PAN Number'),
    aadharCard: checkStringRequiredOrNot(isRequired(setting?.aadharCard), 'Enter Aadhar Number'),
    age: checkStringRequiredOrNot(isRequired(setting?.age), 'Enter age'),
    dob: checkStringRequiredOrNot(isRequired(setting?.dob), 'Enter Date of Birth'),
    postalAddress: checkStringRequiredOrNot(
      isRequired(setting?.postalAddress),
      'Enter Postal Address'
    ),
    notes: checkStringRequiredOrNot(isRequired(setting?.notes), 'Enter Call/Message note'),
  });
};

export const mapAsLeadSchema = object().shape({
  name: string().required('Name is empty'),
  email: string().email(),
  phone: string()
    .nullable(true)
    .matches(
      /^$|^[+(]?\(?\(?\d{1,4}?[-.()\s]?\(?\d{1,3}?\)?[-.()\s]?\d{1,4}[-.()\s]?\d{1,4}[-.()\s]?\d{1,9}[)\d]$/,
      'Please enter valid phone number'
    )
    .nullable(true),
  alternateEmails: array().of(string().email()).nullable(true),
  alternatePhoneNumbers: array()
    .of(
      string()
        .matches(
          /^$|^[+(]?\(?\(?\d{1,4}?[-.()\s]?\(?\d{1,3}?\)?[-.()\s]?\d{1,4}[-.()\s]?\d{1,4}[-.()\s]?\d{1,9}[)\d]$/,
          'Please enter valid phone number'
        )
        .nullable(true)
    )
    .nullable(true),
  socialLinks: array().of(string()).nullable(true),
  currentSalary: getCompensation(false, ''),
  expectedSalary: getCompensation(false, ''),
  otherAllowance: string().nullable(true),
  expectedAllowance: string().nullable(true),
  recruiterActivity: object()
    .shape({
      actions: array().of(string()),
      results: array().of(string()),
    })
    .nullable(true),
  willingToTravel: singleWsButtonGroup.nullable(true),
  availableFrom: date().nullable(true),
  noticePeriodInDays: singleWsButtonGroup.nullable(true),
  authorizationCategory: singleWsButtonGroup.nullable(true),
  //workAuthorization: string().nullable(true),
  nationality: string().nullable(true),
  tags: array().of(string()).nullable(true),
  source: array().of(string()).nullable(true),
  communicationPreference: multipleWsButtonGroup.nullable(true),
  jobPreference: multipleWsButtonGroup.nullable(true),
  currentJobType: singleWsButtonGroup.nullable(true),
  expInYears: number().nullable(true),
  expInMonth: number().nullable(true),
  relExpInYears: number().nullable(true),
  relExpInMonth: number().nullable(true),
  jobTitle: string().nullable(true),
  currentCompany: string().nullable(true),
  prevJobTitles: array().of(string()).nullable(true),
  prevEmployers: array().of(string()).nullable(true),
  language: array()
    .of(
      object().shape({
        language: string(),
        fluency: string(),
      })
    )
    .nullable(true),
});

export const submissionSchema = (
  setting: ISubmissionSettingsResponse | ISubmissionSettingContext | null,
  bu?: boolean
) => {
  const urlPattern = /^(https?:\/\/)?(www\.)?[a-zA-Z0-9-]+\.[a-z]{2,}(\/.*)?$/;

  return (
    setting &&
    object().shape({
      name: checkStringRequiredOrNot(isRequired(setting.name), 'Please enter name'),
      email: checkStringRequiredOrNot(isRequired(setting.email), 'Please enter email').email(
        'Email is invalid'
      ),
      phone: isRequired(setting.phoneNumber)
        ? string()
            .matches(
              /^$|^[+(]?\(?\(?\d{1,4}?[-.()\s]?\(?\d{1,3}?\)?[-.()\s]?\d{1,4}[-.()\s]?\d{1,4}[-.()\s]?\d{1,9}[)\d]$/,
              'Please enter valid phone number'
            )
            .required('Please enter phone number')
        : string()
            .matches(
              /^$|^[+(]?\(?\(?\d{1,4}?[-.()\s]?\(?\d{1,3}?\)?[-.()\s]?\d{1,4}[-.()\s]?\d{1,4}[-.()\s]?\d{1,9}[)\d]$/,
              'Please enter valid phone number'
            )
            .nullable(),
      alternateEmails: checkStringRequiredOrNot(
        isRequired(setting.alternateEmail),
        'please enter alternate email'
      ),

      alternatePhoneNumbers: isRequired(setting.alternatePhoneNumber)
        ? string()
            .matches(
              /^$|^[+(]?\(?\(?\d{1,4}?[-.()\s]?\(?\d{1,3}?\)?[-.()\s]?\d{1,4}[-.()\s]?\d{1,4}[-.()\s]?\d{1,9}[)\d]$/,
              'Please enter valid phone number'
            )
            .required('Please enter alternate phone number')
        : string()
            .matches(
              /^$|^[+(]?\(?\(?\d{1,4}?[-.()\s]?\(?\d{1,3}?\)?[-.()\s]?\d{1,4}[-.()\s]?\d{1,4}[-.()\s]?\d{1,9}[)\d]$/,
              'Please enter valid phone number'
            )
            .nullable(),
      socialLinks: isRequired(setting.linkedInURL)
        ? Yup.array()
            .of(
              Yup.string()
                .matches(urlPattern, 'Please enter a valid URL')
                .required('Please enter a valid URL')
            )
            .required('Please enter social links')
        : Yup.array()
            .of(
              Yup.string()
                .nullable()
                .notRequired()
                .test('is-valid-url', 'Please enter a valid URL', (value) => {
                  return !value || urlPattern.test(value);
                })
            )
            .nullable(true),

      messengerLinks: isRequired(setting.messengerId)
        ? array()
            .of(
              Yup.object().shape({
                messengerId: Yup.string()
                  .required('Messenger ID is required')
                  .trim('Messenger ID cannot contain only spaces'),
                type: Yup.string()
                  .required('Type is required')
                  .trim('Type cannot contain only spaces'),
              })
            )
            .required('Messenger links are required')
            .min(1, 'At least one messenger link is required')
        : messengerSchema.nullable(true),
      currentJobType: singleWsButtonGroupRequiredOrNot(
        isRequired(setting.jobType),
        'Please select job type'
      ),
      skills: checkArrayRequiredOrNot(isRequired(setting.skills), 'Skills are required', 'Skills'),
      preferredLocation: isRequired(setting.preferredLocation)
        ? locationSchema.required('Please enter candidate location')
        : locationSchema.nullable(true),
      currentLocation: isRequired(setting.currentLocation)
        ? locationSchema.required('Please enter candidate location')
        : locationSchema.nullable(true),
      currentJobIncludesTravel: singleWsButtonGroupRequiredOrNot(
        isRequired(setting.currentJobIncludeTravel),
        'Do current job includes travel ?'
      ),
      currentSalary: getCompensation(
        isRequired(setting.currentCompensation),
        'Current salary of candidate is required'
      ),
      expectedSalary: getCompensation(
        isRequired(setting.expectedCompensation),
        'Expected salary of candidate is required'
      ),
      otherAllowance: checkStringRequiredOrNot(
        isRequired(setting.otherAllowance),
        'Other allowances is required'
      ),
      expectedAllowance: checkStringRequiredOrNot(
        isRequired(setting.expectedAllowance),
        'Expected allowances is required'
      ),
      willingToTravel: singleWsButtonGroupRequiredOrNot(
        isRequired(setting.willingToTravel),
        'Is candidate willing to travel ?'
      ),
      availableFrom: isRequired(setting.availableTimeSlot)
        ? date().required('Available From is Required')
        : date().nullable(true),
      noticePeriodInDays: singleWsButtonGroupRequiredOrNot(
        isRequired(setting.noticePeriod),
        'Please select notice period'
      ),
      interviewingWithOthers: singleWsButtonGroupRequiredOrNot(
        isRequired(setting.interviewingAnywhereElse),
        'Is candidate interviewing else ?'
      ),
      // authorizationCategory: singleWsButtonGroupRequiredOrNot(
      //   isRequired(setting.workAuthorizationCategory),
      //   'Please select work authorization category'
      // ),
      // workAuthorization: checkStringRequiredOrNot(
      //   isRequired(setting.willingToTravel),
      //   'Enter work authorization'
      // ),
      visaStatus: checkStringRequiredOrNot(
        isRequired(setting.workAuthorization && setting.visaType),
        'Enter visa status'
      ),
      visaValidity: checkStringRequiredOrNot(
        isRequired(setting.visaValidity),
        'Enter visa validity'
      ),
      // citizenship: string().nullable(true),
      nationality: checkStringRequiredOrNot(
        isRequired(setting.nationality),
        'Nationality is required'
      ),
      candidateId: checkStringRequiredOrNot(
        isRequired(setting.candidateID),
        'Please enter candidate id'
      ),
      communicationPreference: isRequired(setting.communicationPreferences)
        ? multipleWsButtonGroup.required('Please select communication preference')
        : multipleWsButtonGroup.nullable(true),
      jobPreference: isRequired(setting.jobPreferences)
        ? multipleWsButtonGroup.required('Please select job preference')
        : multipleWsButtonGroup.nullable(true),
      expInYears: isRequired(setting.totalExperience)
        ? number().required('Please enter total experience').min(0)
        : number().transform((value) =>
            isNaN(value) || value === null || value === undefined ? 0 : value
          ),
      expInMonth: number().transform((value) =>
        isNaN(value) || value === null || value === undefined ? 0 : value
      ),
      relExpInYears: isRequired(setting.relevantExperience)
        ? number().required('Please enter relevant experience').min(0)
        : number().transform((value) =>
            isNaN(value) || value === null || value === undefined ? 0 : value
          ),
      relExpInMonth: number().transform((value) =>
        isNaN(value) || value === null || value === undefined ? 0 : value
      ),
      jobTitle: checkStringRequiredOrNot(
        isRequired(setting.currentJobTitle),
        'Please enter candidate job title'
      ),
      currentCompany: checkStringRequiredOrNot(
        isRequired(setting.currentEmployer),
        'Please enter candidate current employer'
      ),
      jobChangeReason: checkStringRequiredOrNot(
        isRequired(setting.reasonForChange),
        'Please enter reason for change'
      ),
      recruiterAssessment: checkStringRequiredOrNot(
        isRequired(setting.yourAssessment),
        'Please enter your assessment'
      ),
      relocationPreferences: checkStringRequiredOrNot(
        isRequired(setting.relocationPreferences),
        'Please enter relocation preference'
      ),
      willingToTravelType: checkStringRequiredOrNot(false, 'Is candidate willing to travel'),
      familyWillingToRelocate: singleWsButtonGroupRequiredOrNot(
        isRequired(setting.familyWillingToRelocate),
        'Is family willing to relocate'
      ),
      prevJobTitles: checkArrayRequiredOrNot(
        isRequired(setting.previousJobTitles),
        'Previous job titles are required',
        'Previous Job Titles'
      ),
      prevEmployers: checkArrayRequiredOrNot(
        isRequired(setting.previousEmployers),
        'Previous Employers are required',
        'Previous employer'
      ),
      highestEducation: checkArrayRequiredOrNot(
        isRequired(setting.highestEducation),
        'Highest Education is required',
        'Highest Education'
      ),
      institutesAttended: checkArrayRequiredOrNot(
        isRequired(setting.institutesAttended),
        'Institue attended is required',
        'Institute attended'
      ),
      certifications: isRequired(setting.certifications)
        ? array().of(string()).required()
        : array().of(string()).nullable(true),
      buId: checkStringRequiredOrNot(
        bu ? isRequired(setting.businessUnit) : false,
        'Please select business Unit'
      ),
      clientBillRate: getCompensation(
        isRequired(setting.clientBillRate),
        'Client Bill Rate of candidate is required'
      ),
      offeredPayRate: getCompensation(
        isRequired(setting.offeredPayRate),
        'Offered Salary/Payrate of candidate is required'
      ),
      aadharNumber: checkStringRequiredOrNot(
        isRequired(setting.aadharNumber),
        'Aadhar Number is required'
      ),
      panCard: checkStringRequiredOrNot(
        isRequired(setting.panCard),
        'Pan card details are required'
      ),
      ssnNumber: checkStringRequiredOrNot(isRequired(setting.ssnNumber), 'SSN number is required'),
      postalAddress: checkStringRequiredOrNot(
        isRequired(setting?.postalAddress),
        'Enter Postal Address'
      ),
      age: isRequired(setting.age)
        ? number()
            .typeError('Please enter age')
            .required('Please enter age')
            .min(0, 'age greater than 0')
            .max(100, 'age less than 100 ')
        : number().transform((value) =>
            isNaN(value) || value === null || value === undefined || value === '' ? 0 : value
          ),
      dob: isRequired((setting as ISubmissionSettingsResponse).DOB)
        ? date().required('Date of Birth is required')
        : date().nullable(true),
      gender: isRequired(setting.gender)
        ? string()
            .required('Gender is required')
            .notOneOf(['NOT_SPECIFIED'], 'Gender should be selected')
        : string().nullable(true),
      passportNumber: checkStringRequiredOrNot(
        isRequired(setting.passportNumber),
        'Passport Number is required'
      ),
      passportType: checkStringRequiredOrNot(
        isRequired(setting.passportType),
        'PassportType is required'
      ),
      skillMatrixRatings: isRequired((setting as ISubmissionSettingsResponse).skillMatrix)
        ? Yup.array()
            .of(
              Yup.object({
                skill: Yup.string().required('skill Name is required'),
                rating: Yup.number().required('rating is required'),
              })
            )
            .min(1, 'At least one skill is required')
        : skillMatrixSchema,
      exEmployeeOfClient: singleWsButtonGroupRequiredOrNot(
        isRequired((setting as ISubmissionSettingsResponse).exEmployeeOfClient),
        'Ex Employee of Client is reqiured'
      ),
      exEmployeeId: checkStringRequiredOrNot(
        isRequired((setting as ISubmissionSettingsResponse).exEmployeeId),
        'Ex Employee Id is required'
      ),
      portalId: checkStringRequiredOrNot(
        isRequired((setting as ISubmissionSettingsResponse).portalId),
        'portal Id is required'
      ),
      workAuthValidity: isRequired((setting as ISubmissionSettingsResponse).workAuthValidity)
        ? date().required('Work Authorization validity is required')
        : date().nullable(true),
      // payRateExpenses: getCompensation(
      //   isRequired(setting.payRateExpenses),
      //   'Pay Rate Expenses is required'
      // ),
    })
  );
};
export const submissionSchemaForBulk = (
  setting: ISubmissionSettingsResponse | ISubmissionSettingContext,
  bu?: boolean
) => {
  return object().shape({
    name: checkStringRequiredOrNot(isRequired(setting.name), 'Please enter name'),
    email: checkStringRequiredOrNot(isRequired(setting.email), 'Please enter email'),
    phoneNumber: checkStringRequiredOrNot(
      isRequired(setting.phoneNumber),
      'Please enter phone number'
    ),
    alternateEmails: isRequired(setting.alternateEmail)
      ? array().of(string()).required('Please enter alternate emails')
      : array().of(string()).nullable(true),
    alternatePhoneNumbers: isRequired(setting.alternatePhoneNumber)
      ? array()
          .of(
            string().matches(
              /^$|^[+(]?\(?\(?\d{1,4}?[-.()\s]?\(?\d{1,3}?\)?[-.()\s]?\d{1,4}[-.()\s]?\d{1,4}[-.()\s]?\d{1,9}[)\d]$/,
              'Please enter valid phone number. The format of the preselected or entered phone number should be correct'
            )
          )
          .required('Please enter alternate phone number')
      : array()
          .of(
            string().matches(
              /^$|^[+(]?\(?\(?\d{1,4}?[-.()\s]?\(?\d{1,3}?\)?[-.()\s]?\d{1,4}[-.()\s]?\d{1,4}[-.()\s]?\d{1,9}[)\d]$/,
              'Please enter valid phone number. The format of the preselected or entered phone number should be correct'
            )
          )
          .nullable(true),
    socialLinks: isRequired(setting.linkedInURL)
      ? array().of(string()).required('Please enter social links')
      : array().of(string()).nullable(true),
    messengerLinks: isRequired(setting.messengerId)
      ? messengerSchema.required('Please enter messenger id')
      : messengerSchema.nullable(true),
    currentJobType: singleWsButtonGroupRequiredOrNot(
      isRequired(setting.jobType),
      'Please select job type'
    ),
    skills: isRequired(setting.skills)
      ? array().of(string()).required('Please enter candidate skills')
      : array().of(string()).nullable(true),
    preferredLocation: locationSchema.nullable(true),
    currentLocation: isRequired(setting.currentLocation)
      ? locationSchema.required('Please enter candidate location')
      : locationSchema.nullable(true),
    currentJobIncludesTravel: singleWsButtonGroupRequiredOrNot(
      isRequired(setting.currentJobIncludeTravel),
      'Do current job includes travel ?'
    ),
    currentSalary: getCompensation(
      isRequired(setting.currentCompensation),
      'Current salary of candidate is required'
    ),
    expectedSalary: getCompensation(
      isRequired(setting.expectedCompensation),
      'Expected salary of candidate is required'
    ),
    otherAllowance: checkStringRequiredOrNot(
      isRequired(setting.otherAllowance),
      'Other allowances is required'
    ),
    expectedAllowance: checkStringRequiredOrNot(
      isRequired(setting.expectedAllowance),
      'Expected allowances is required'
    ),
    willingToTravel: singleWsButtonGroupRequiredOrNot(
      isRequired(setting.willingToTravel),
      'Is candidate willing to travel ?'
    ),
    availableFrom: isRequired(setting.availableTimeSlot)
      ? date().required()
      : date().nullable(true),
    noticePeriodInDays: singleWsButtonGroupRequiredOrNot(
      isRequired(setting.noticePeriod),
      'Please select notice period'
    ),
    interviewingWithOthers: singleWsButtonGroupRequiredOrNot(
      isRequired(setting.interviewingAnywhereElse),
      'Is candidate interviewing else ?'
    ),
    // authorizationCategory: singleWsButtonGroupRequiredOrNot(
    //   isRequired(setting.workAuthorizationCategory),
    //   'Please select work authorization category'
    // ),
    // workAuthorization: checkStringRequiredOrNot(
    //   isRequired(setting.willingToTravel),
    //   'Enter work authorization'
    // ),
    visaStatus: checkStringRequiredOrNot(
      isRequired(setting.workAuthorization && setting.visaType),
      'Enter visa status'
    ),
    visaValidity: checkStringRequiredOrNot(isRequired(setting.visaValidity), 'Enter visa validity'),
    // citizenship: string().nullable(true),
    nationality: string().nullable(true),
    tags: array().of(string()).nullable(true),
    // source: checkStringRequiredOrNot(isRequired(setting.source), 'Please enter source'),
    candidateId: checkStringRequiredOrNot(
      isRequired(setting.candidateID),
      'Please enter candidate id'
    ),
    communicationPreference: isRequired(setting.communicationPreferences)
      ? multipleWsButtonGroup.required('Please select communication preference')
      : multipleWsButtonGroup.nullable(true),
    jobPreference: isRequired(setting.jobPreferences)
      ? multipleWsButtonGroup.required('Please select job preference')
      : multipleWsButtonGroup.nullable(true),
    expInYears: isRequired(setting.totalExperience)
      ? number().required('Please enter total experience')
      : number().transform((value) =>
          isNaN(value) || value === null || value === undefined ? 0 : value
        ),
    expInMonth: number().transform((value) =>
      isNaN(value) || value === null || value === undefined ? 0 : value
    ),
    relExpInYears: isRequired(setting.relevantExperience)
      ? number().required('Please enter relevant experience')
      : number().transform((value) =>
          isNaN(value) || value === null || value === undefined ? 0 : value
        ),
    relExpInMonth: number().transform((value) =>
      isNaN(value) || value === null || value === undefined ? 0 : value
    ),
    jobTitle: checkStringRequiredOrNot(
      isRequired(setting.currentJobTitle),
      'Please enter candidate job title'
    ),
    currentCompany: checkStringRequiredOrNot(
      isRequired(setting.currentEmployer),
      'Please enter candidate current employer'
    ),
    jobChangeReason: checkStringRequiredOrNot(
      isRequired(setting.reasonForChange),
      'Please enter reason for change'
    ),
    assessment: checkStringRequiredOrNot(
      isRequired(setting.yourAssessment),
      'Please enter your assessment'
    ),
    relocationPreferences: checkStringRequiredOrNot(
      isRequired(setting.relocationPreferences),
      'Please enter relocation preference'
    ),
    willingToTravelType: checkStringRequiredOrNot(false, 'Is candidate willing to travel'),
    familyWillingToRelocate: singleWsButtonGroupRequiredOrNot(
      isRequired(setting.familyWillingToRelocate),
      'Is family willing to relocate'
    ),
    prevJobTitles: isRequired(setting.currentEmployer)
      ? array().of(string()).required()
      : array().of(string()).nullable(true),
    prevEmployers: isRequired(setting.previousEmployers)
      ? array().of(string()).required()
      : array().of(string()).nullable(true),
    highestEducation: isRequired(setting.highestEducation)
      ? array().of(string()).required()
      : array().of(string()).nullable(true),
    institutesAttended: isRequired(setting.institutesAttended)
      ? array().of(string()).required()
      : array().of(string()).nullable(true),
    certifications: isRequired(setting.certifications)
      ? array().of(string()).required()
      : array().of(string()).nullable(true),
    buId: checkStringRequiredOrNot(
      bu ? isRequired(setting.businessUnit) : false,
      'Please select business Unit'
    ),
  });
};

export const getMandatoryFields = (setting, bu = false) => {
  const mandatoryFields: string[] = [];
  const shownFields: string[] = [];

  const processField = (
    fieldSetting: ISubmissionSettingsObject,
    mandatoryKey: string,
    shownKey = ''
  ) => {
    if (isRequired(fieldSetting)) {
      mandatoryFields.push(mandatoryKey);
      if (!isEmpty(shownKey)) {
        shownFields.push(shownKey);
      }
    }
  };
  if (setting) {
    processField(setting.name, 'name');
    processField(setting.email, 'email');
    processField(setting.phoneNumber, 'phone');
    processField(setting.alternateEmails, 'alternateEmails');
    processField(setting.alternatePhoneNumbers, 'alternatePhoneNumbers');
    processField(setting.linkedInURL, 'socialLinks');
    processField(setting.messengerId, 'messengerLinks');
    processField(setting.jobType, 'currentJobType');
    processField(setting.skills, 'skills');
    processField(setting.currentLocation, 'currentLocation');
    processField(setting.currentJobIncludeTravel, 'currentJobIncludesTravel');
    processField(setting.currentCompensation, 'currentSalary');
    processField(setting.expectedCompensation, 'expectedSalary');
    processField(setting.otherAllowance, 'otherAllowance');
    processField(setting.expectedAllowance, 'expectedAllowance');
    processField(setting.willingToTravel, 'willingToTravel');
    processField(setting.availableTimeSlot, 'availableFrom');
    processField(setting.noticePeriod, 'noticePeriodInDays');
    processField(setting.interviewingAnywhereElse, 'interviewingWithOthers');
    // processField(setting.workAuthorizationCategory, 'authorizationCategory');
    // processField(setting.visaType, 'visaStatus');
    processField(setting.visaValidity, 'visaValidity');
    // processField(setting.source, 'source');
    processField(setting.candidateID, 'candidateId');
    processField(setting.communicationPreferences, 'communicationPreference');
    processField(setting.jobPreferences, 'jobPreference');
    processField(setting.totalExperience, 'expInYears', 'Total experience');
    processField(setting.relevantExperience, 'relExpInYears', 'Relevant experience');
    processField(setting.currentJobTitle, 'jobTitle', 'Latest Job Title');
    processField(setting.currentEmployer, 'currentCompany', 'Latest Employer');
    processField(setting.reasonForChange, 'jobChangeReason');
    processField(setting.yourAssessment, 'recruiterAssessment');
    processField(setting.relocationPreferences, 'relocationPreferences');
    processField(setting.familyWillingToRelocate, 'familyWillingToRelocate');
    processField(setting.previousEmployers, 'prevEmployers', 'Previous Employers');
    processField(setting.previousJobTitles, 'prevJobTitles', 'Previous Job Titles');
    processField(setting.highestEducation, 'highestEducation', 'Highest education qualifications');
    processField(setting.institutesAttended, 'institutesAttended', 'Institutions Attended');
    processField(setting.certifications, 'certifications');
    processField(setting.aadharNumber, 'aadharNumber');
    processField(setting.panCard, 'panCard');
    processField(setting.ssnNumber, 'ssnNumber');
    processField(setting.postalAddress, 'postalAddress');
    processField(setting.age, 'age');
    processField(setting.dob, 'dob');
    processField(setting.gender, 'gender');
    processField(setting.passportNumber, 'passportNumber');
    processField(setting.passportType, 'passportType');
    processField(setting.nationality, 'nationality');
    processField(setting.otherDocuments, 'otherDocuments');
    processField(setting.uploadCV, 'cvUpload');
    processField(setting.skillMatrix, 'skillMatrixRatings');
    processField(setting.exEmployeeId, 'exEmployeeId');
    processField(setting.portalId, 'portalId');
    processField(setting.workAuthValidity, 'workAuthValidity');
    processField(setting.exEmployeeOfClient, 'exEmployeeOfClient');

    if (bu) {
      processField(setting.businessUnit, 'buId');
    }

    processField(setting.clientBillRate, 'clientBillRate');
    processField(setting.offeredPayRate, 'offeredPayRate');
  }

  return { mandatoryFields, shownFields };
};
